import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid2,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import {
  updateAdvisiorReview,
  createAdvisiorReview,
  utilitiesAdvisorReview as utilitiesAdvisorReviewApi,
  detailAdvisorReview,
  sanitizeLabel,
} from "../../../../apis/apiAdvisorReview";
import { ChipInfo } from "../../../MainComponents/ChipInfo/ChipInfo";

import hexAr from "../../../../assets/image/flags/hx-ar.svg";
import hexBo from "../../../../assets/image/flags/hx-bo.svg";
import hexBr from "../../../../assets/image/flags/hx-br.svg";
import hexCa from "../../../../assets/image/flags/hx-ca.svg";
import hexCl from "../../../../assets/image/flags/hx-cl.svg";
import hexCo from "../../../../assets/image/flags/hx-co.svg";
import hexCr from "../../../../assets/image/flags/hx-cr.svg";
import hexDe from "../../../../assets/image/flags/hx-de.svg";
import hexEc from "../../../../assets/image/flags/hx-ec.svg";
import hexMx from "../../../../assets/image/flags/hx-mx.svg";
import hexSv from "../../../../assets/image/flags/hx-sv.svg";
import hexUs from "../../../../assets/image/flags/hx-us.svg";

// import hexMx from '../../../../assets/image/flags/hx-mx.svg';
// import hexUs from '../../../../assets/image/flags/us.png';
// import hexHo from '../../../../assets/image/flags/ho.png';
import Icon from "../../../Icon/Icon";
import { EGButton } from "../../../MainComponents/Button/EGButton/EGButton";
import { EGBasicSelect } from "../../../MainComponents/Select/EGBasicSelect";

/**
 * Funcion para obtener los textos por valor en la lista de utils de la revision del asesor
 * @param {Array} utilList Lista de los utilities que donde se va a obtener la informacion
 * @param {*} value El valor actual que tiene la revision del asesor
 * @param {string} valueKey La llave de el valor que hay que buscar
 * @param {string} labelKey La llave del label que queremos mostrar
 * @returns {string} Label del valor actual
 */

/** Utilidad genérica para buscar un label en una lista */
const getLabelFromUtilities = (list, value, valueKey, labelKey) =>
  Array.isArray(list)
    ? list.find((i) => i?.[valueKey] === value)?.[labelKey]
    : undefined;

/** Checkbox individual */
const CheckboxReview = ({ item, handleChange, target, disabled }) => {
  const theme = useTheme();
  return (
    <FormGroup key={item.value}>
      <FormControlLabel
        label={item.question}
        sx={{
          "& .MuiTypography-root": {
            fontSize: 16,
            fontWeight: 600,
            color: theme.palette.secondary.main,
          },
        }}
        control={
          <Checkbox
            sx={{ "& .MuiSvgIcon-root": { fontSize: 20 } }}
            checked={!!item.is_checked}
            onChange={(e) => handleChange(target, item.value, e.target.checked)}
            disabled={disabled}
          />
        }
      />
    </FormGroup>
  );
};

/** Config para selects y chips */
const FIELD_CONFIG = (t, theme) => ({
  country: {
    label: t("AdvisorReview.country"),
    icon: (flag) => <FlagIcon flag={flag} />,
  },
  language: {
    label: t("AdvisorReview.language"),
    icon: (
      <div style={{ display: "grid", placeContent: "center" }}>
        <Icon name="outline_translate" size={16} />
      </div>
    ),
  },
  severity: {
    label: t("AdvisorReview.severity"),
    icon: (
      <div style={{ display: "grid", placeContent: "center" }}>
        <Icon name="bold_flash_2" size={16} />
      </div>
    ),
  },
  call_tone: { label: t("AdvisorReview.tone_percived") },
  comment: { label: t("AdvisorReview.comments") },
});

/** Icono de bandera */
const FlagIcon = ({ flag }) => {
  const icons = {
    ARG: hexAr,
    BOL: hexBo,
    BRA: hexBr,
    CAN: hexCa,
    CHL: hexCl,
    COL: hexCo,
    CRI: hexCr,
    ECU: hexEc,
    MEX: hexMx,
    SLV: hexSv,
    USA: hexUs,
  };
  if (!flag || !icons[flag]) {
    return (
      <div style={{ display: "grid", placeContent: "center" }}>
        <Icon name="outline_location" size={16} />
      </div>
    );
  }
  return <img src={icons[flag]} alt={flag} style={{ width: 30, height: "auto" }} />;
};

/** Sanitizadores */
const sanitizeLanguage = (lang) =>
  !lang
    ? undefined
    : lang.toLowerCase().includes("spanish; castilian")
    ? "spanish"
    : lang.toLowerCase();
const sanitizeSeverity = (sev) =>
  sev ? sev.charAt(0).toUpperCase() + sev.slice(1).toLowerCase() : undefined;

export const AdvisorReview = ({ report_id, edit = false, data }) => {
  const { i18n, t } = useTranslation("information");
  const theme = useTheme();
  const lang = i18n?.language;

  const [utils, setUtils] = useState(null);
  const [review, setReview] = useState(data?.review ?? null);
  const [fields, setFields] = useState({
    country: null,
    language: null,
    severity: null,
    event: null,
    call_tone: null,
    comment: "",
  });
  const [questions, setQuestions] = useState({
    params: [],
    extras: [],
  });
  const [errors, setErrors] = useState({});

  /** Fetch utilidades */
  useEffect(() => {
    utilitiesAdvisorReviewApi()
      .then((resp) => setUtils(resp?.data ?? null))
      .catch((err) => console.error("utilitiesAdvisorReviewApi error", err));
  }, [report_id]);

  /** Inicializa datos cuando llegan utils y review */
  useEffect(() => {
    if (!utils) return;

    if (review) {
      setQuestions({
        params: buildQuestions(
          utils?.questions_of_params,
          review?.questions_of_params
        ),
        extras: buildQuestions(
          utils?.questions_of_extra_details,
          review?.questions_of_extra_details
        ),
      });

      const countryObj = utils?.list_countries?.find(
        (c) => c.value === review.country
      );
      const countryName =
        countryObj?.locale?.[lang] ||
        countryObj?.locales?.[lang] ||
        review?.country ||
        t("AdvisorReview.undefined");

      const languageLabel =
        getLabelFromUtilities(
          utils?.list_languages,
          review.language,
          "code",
          "name"
        ) ||
        review.language ||
        null;

      setFields({
        country: countryName,
        language: !edit
          ? t(`AdvisorReview.${sanitizeLanguage(languageLabel)}`)
          : languageLabel,
        severity: !edit
          ? sanitizeSeverity(
              review?.severity
                ? t(`AdvisorReview.${review.severity}`)
                : t("AdvisorReview.undefined")
            )
          : review?.severity,
        event: review?.event ?? null,
        call_tone: !edit
          ? review?.call_tone ?? t("AdvisorReview.undefined")
          : review?.call_tone,
        comment:
          review?.comment ?? (edit ? "" : t("AdvisorReview.without_comments")),
      });
    }
  }, [utils, review, lang, edit, t]);

  /** Construye preguntas con sus respuestas */
  const buildQuestions = (utilList = [], answers = {}) =>
    (utilList || []).map((item) => ({
      ...item,
      is_checked:
        !!answers?.[item.value]?.is_checked ?? !!answers?.[item.value],
    }));

  /** Validación compacta */
  const validate = () => {
    const required = [
      "country",
      "language",
      "severity",
      "event",
      "call_tone",
      "comment",
    ];
    const errs = required.reduce((acc, key) => {
      if (!fields[key]) acc[key] = [t("AdvisorReview.field_must_be_complete")];
      return acc;
    }, {});
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  /** Handlers */
  const handleFieldChange = (key, value) =>
    setFields((f) => ({ ...f, [key]: value }));
  const handleQuestionChange = (target, value, checked) =>
    setQuestions((prev) => ({
      ...prev,
      [target]: prev[target].map((item) =>
        item.value === value ? { ...item, is_checked: checked } : item
      ),
    }));

  /** Guardar review */
  const saveReview = async () => {
    if (!validate()) return;

    const questionsPayload = (arr) =>
      arr.reduce((acc, i) => ({ ...acc, [i.value]: !!i.is_checked }), {});

    const payload = {
      report_id,
      ...(review?.id && { review_id: review.id }),
      ...fields,
      event: fields.event ?? "1 evento",
      questions_of_params: questionsPayload(questions.params),
      questions_of_extra_details: questionsPayload(questions.extras),
    };

    try {
      review?.id
        ? await updateAdvisiorReview(payload)
        : await createAdvisiorReview(payload);
    } catch (err) {
      console.error("saveAdvisorReview error", err);
    }
  };

  /** Helpers memorizados */
  const selectLists = useMemo(
    () => ({
      severity:
        utils?.list_severity?.map((v) => ({ label: v, value: v })) ?? [],
      event: utils?.list_events?.map((v) => ({ label: v, value: v })) ?? [],
      call_tone:
        utils?.list_call_tone?.map((v) => ({ label: v, value: v })) ?? [],
      countries: utils?.list_countries ?? [],
      languages: utils?.list_languages ?? [],
    }),
    [utils]
  );

  /** Render */

  if (!edit && !review)
    return <Typography>{t("AdvisorReview.no_comments")}</Typography>;
  if (edit && !utils) return null;

  const fieldConfig = FIELD_CONFIG(t, theme);

  return (
    <>
      {/* Chips */}
      {!edit && (
        <Stack spacing={2} direction="row" flexWrap="wrap" sx={{ p: 1 }}>
          {["country", "language", "severity"].map((key) => (
            <ChipInfo
              key={key}
              label={
                <span>
                  {fieldConfig[key].label}: <b>{fields[key]}</b>
                </span>
              }
              icon={
                key === "country"
                  ? fieldConfig[key].icon(review?.country)
                  : fieldConfig[key].icon
              }
              variant="outlined"
              color={theme.palette.blueGray200.main}
              maxWidth={200}
            />
          ))}
        </Stack>
      )}

      {/* Selects */}
      {edit && (
        <Grid2 container spacing={2} sx={{ p: 1 }}>
          <Grid2 xs={12} md={6}>
            <EGBasicSelect
              label={t("AdvisorReview.country")}
              value={fields.country}
              options={selectLists.countries}
              onChange={(e) => handleFieldChange("country", e.target.value)}
            />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <EGBasicSelect
              label={t("AdvisorReview.language")}
              value={fields.language}
              options={selectLists.languages}
              optionLabel="name"
              onChange={(e) => handleFieldChange("language", e.target.value)}
            />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <EGBasicSelect
              label={t("AdvisorReview.severity")}
              value={fields.severity}
              options={selectLists.severity}
              onChange={(e) => handleFieldChange("severity", e.target.value)}
            />
          </Grid2>
          <Grid2 xs={12} md={6}>
            <EGBasicSelect
              label={t("AdvisorReview.tone")}
              value={fields.call_tone}
              options={selectLists.call_tone}
              onChange={(e) => handleFieldChange("call_tone", e.target.value)}
            />
          </Grid2>
        </Grid2>
      )}

      {/* Preguntas */}
      <Stack spacing={0.8} sx={{ p: 1 }}>
        {Object.entries(questions).map(([target, list]) =>
          list.map((item) => (
            <CheckboxReview
              key={item.value}
              item={{
                ...item,
                question: t(`${target}.${item.question}`),
              }}
              handleChange={handleQuestionChange}
              target={target}
              disabled={!edit}
            />
          ))
        )}
      </Stack>

      {/* Comentarios */}
      {!edit ? (
        <>
          {["call_tone", "comment"].map((key) => (
            <Box key={key} p={1}>
              <Typography
                variant="h4"
                sx={{ fontSize: 14, color: theme.palette.primary.main }}
              >
                {fieldConfig[key].label}
              </Typography>
              <Typography
                variant="body1"
                component="p"
                sx={{
                  fontSize: 16,
                  borderBottom: `1px solid ${theme.palette.blueGray200.main}`,
                }}
              >
                {fields[key]}
              </Typography>
            </Box>
          ))}
        </>
      ) : (
        <Box>
          <TextField
            label={t("AdvisorReview.comments")}
            value={fields.comment}
            onChange={(e) => handleFieldChange("comment", e.target.value)}
            rows={4}
            fullWidth
            multiline
            sx={{ mt: 3 }}
          />
        </Box>
      )}

      {edit && (
        <EGButton
          label={t("information.save")}
          variant="outlined"
          onClick={saveReview}
          sx={{ mt: 1 }}
        />
      )}
    </>
  );
};
